/**
 * React Query hooks for Cloudflare Pages deployment.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLiveConnection } from './useLiveEvents';
import { cloudflareApi } from '../api/cloudflare';
import type {
  CloudflareAccount,
  CloudflareProject,
  CloudflareDeployment,
  VerifyCloudflareTokenRequest,
  CreateCloudflareProjectRequest,
  DeployToCloudflareRequest,
  AddCloudflareDomainRequest,
} from '../types';

/** Query keys */
const CLOUDFLARE_KEYS = {
  projects: ['cloudflare', 'projects'] as const,
  domainStatus: ['cloudflare', 'domain-status'] as const,
  deployments: ['cloudflare', 'deployments'] as const,
  deployment: (id: string) => ['cloudflare', 'deployments', id] as const,
};

/** Hook to verify a Cloudflare API token */
export function useVerifyCloudflareToken() {
  return useMutation({
    mutationFn: async (data: VerifyCloudflareTokenRequest): Promise<CloudflareAccount[]> => {
      const response = await cloudflareApi.verifyToken(data);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message ?? 'Failed to verify token');
      }
      return response.data;
    },
  });
}

/** Hook to list Cloudflare Pages projects */
export function useCloudflareProjects(siteSlug?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...CLOUDFLARE_KEYS.projects, { siteSlug: siteSlug ?? null }],
    enabled: siteSlug !== '' && (options?.enabled ?? true),
    queryFn: async (): Promise<CloudflareProject[]> => {
      const response = await cloudflareApi.listProjects(siteSlug);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message ?? 'Failed to list projects');
      }
      return response.data;
    },
  });
}

/** Hook to create a Cloudflare Pages project */
export function useCreateCloudflareProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCloudflareProjectRequest): Promise<CloudflareProject> => {
      const response = await cloudflareApi.createProject(data);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message ?? 'Failed to create project');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLOUDFLARE_KEYS.projects });
    },
  });
}

/** Select a saved deployment target without exposing its API token. */
export function useSelectCloudflareProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { site_slug?: string; project_name: string }) => {
      const response = await cloudflareApi.selectProject(data);
      if (!response.success || !response.data) throw new Error(response.error?.message ?? 'Could not select project');
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cloudflare'] }),
  });
}

/** Hook to deploy to Cloudflare Pages */
export function useDeployToCloudflare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data?: DeployToCloudflareRequest): Promise<CloudflareDeployment> => {
      const response = await cloudflareApi.deploy(data);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message ?? 'Deployment failed');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLOUDFLARE_KEYS.deployments });
    },
  });
}

/** Hook to add a custom domain to a CF Pages project */
export function useAddCloudflareDomain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddCloudflareDomainRequest): Promise<{ domain: string; project_name: string; dns_updated: boolean }> => {
      const response = await cloudflareApi.addDomain(data);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message ?? 'Failed to add domain');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLOUDFLARE_KEYS.projects });
      queryClient.invalidateQueries({ queryKey: CLOUDFLARE_KEYS.domainStatus });
    },
  });
}

/**
 * Hook to get domain verification status from CF Pages.
 * Pending domains poll every 5s unless a connected stream has a server observer receipt.
 */
export function useCloudflareDomainStatus(options?: { enabled?: boolean; siteSlug?: string }) {
  const connected = useLiveConnection((state) => state.connected);
  return useQuery({
    queryKey: [...CLOUDFLARE_KEYS.domainStatus, { siteSlug: options?.siteSlug ?? null }],
    queryFn: async () => {
      const response = await cloudflareApi.getDomainStatus(options?.siteSlug);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message ?? 'Failed to get domain status');
      }
      return { domains: response.data, liveObservation: response.meta?.live_observation === true };
    },
    select: (result) => result.domains,
    enabled: options?.siteSlug !== '' && (options?.enabled ?? true),
    refetchInterval: (query) => {
      if (query.state.status === 'error') return 5000;
      const domains = query.state.data?.domains;
      if (connected && query.state.status === 'success' && query.state.data?.liveObservation) return false;
      if (!domains || domains.length === 0) return false;
      const anyPending = domains.some((d) => d.status !== 'active');
      return anyPending ? 5000 : false;
    },
  });
}

/** Hook to list deployment history */
export function useCloudflareDeployments(siteSlug?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...CLOUDFLARE_KEYS.deployments, { siteSlug: siteSlug ?? null }],
    enabled: siteSlug !== '' && (options?.enabled ?? true),
    queryFn: async (): Promise<CloudflareDeployment[]> => {
      const response = await cloudflareApi.listDeployments(siteSlug);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message ?? 'Failed to list deployments');
      }
      return response.data;
    },
  });
}

/** Hook to get a specific deployment */
export function useCloudflareDeployment(deploymentId: string, options?: { enabled?: boolean; siteSlug?: string }) {
  return useQuery({
    queryKey: [...CLOUDFLARE_KEYS.deployment(deploymentId), { siteSlug: options?.siteSlug ?? null }],
    queryFn: async (): Promise<CloudflareDeployment> => {
      const response = await cloudflareApi.getDeployment(deploymentId, options?.siteSlug);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message ?? 'Failed to get deployment');
      }
      return response.data;
    },
    enabled: !!deploymentId && options?.siteSlug !== '' && (options?.enabled ?? true),
    refetchInterval: (query) => ['queued', 'building'].includes(query.state.data?.status ?? '') ? 5000 : false,
  });
}
